import type { FC, ReactElement } from 'react';
import { Fragment, useContext, useMemo, useState } from 'react';

import { Box } from '@/components/Box';
import { Loader } from '@/components/Loader';
import type { ContentfulPageBoutiqueDetailContentItem } from '@/graphql/contentful/api';
import { useRouter } from '@/hooks/useRouter';
import type { StoreHit } from '@/integrations/algolia/types';
import type { CmsExtraDataRecord } from '@/integrations/contentful';
import { generateSectionKey } from '@/integrations/contentful';
import type { ContentfulData } from '@/integrations/contentful/types';
import { Default } from '@/layouts/Default';
import {
  buildBoutiqueStoreJsonLd,
  generateLocalBusinessSchema,
} from '@/lib/seo/StructuredData';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';

import { renderContentfulBoutiqueDetailSection } from './cms';

export type cmsBoutiqueDetailItemType =
  | ContentfulData
  | ContentfulPageBoutiqueDetailContentItem;

// Updated props to accept full store data from server
export type BoutiqueDetailProps = {
  cmsBoutiqueDetailItems: cmsBoutiqueDetailItemType[];
  cmsExtraData: CmsExtraDataRecord;
  store: StoreHit;
};

type BoutiqueDetailViewWrapperType = FC<{
  children?: React.ReactNode;
  cmsBoutiqueDetailItems: cmsBoutiqueDetailItemType[];
  cmsExtraData: CmsExtraDataRecord;
  store: StoreHit;
}> & {
  getLayout?: (page: ReactElement) => ReactElement;
};

export const BoutiqueDetailViewWrapper: BoutiqueDetailViewWrapperType = ({
  cmsBoutiqueDetailItems,
  cmsExtraData,
  store,
}) => {
  return (
    <BoutiqueDetailView
      cmsBoutiqueDetailItems={cmsBoutiqueDetailItems}
      cmsExtraData={cmsExtraData}
      store={store}
    />
  );
};

export const BoutiqueDetailView = ({
  cmsBoutiqueDetailItems,
  cmsExtraData,
  store,
}: BoutiqueDetailProps) => {
  const draftMode = useContext(DraftModeContext);
  const [loading] = useState<boolean>(false);
  const { locale, paths, push, query } = useRouter();

  // Guard: if store is missing, redirect to 404
  if (!store) {
    push(paths.notFound.url());
  }

  const structuredData = useMemo(() => {
    return store
      ? generateLocalBusinessSchema(store, locale, process.env.NEXT_PUBLIC_URL)
      : null;
  }, [store]);

  const isLocalized = query.type === 'googleMap';

  return loading ? (
    <Loader height={400} />
  ) : (
    <>
      <Box>
        <>
          {cmsBoutiqueDetailItems.map(
            (data: cmsBoutiqueDetailItemType, i: number) => (
              <Fragment key={generateSectionKey(data as ContentfulData, i)}>
                {renderContentfulBoutiqueDetailSection(
                  data,
                  isLocalized,
                  store as StoreHit,
                  cmsExtraData,
                  draftMode,
                )}
              </Fragment>
            ),
          )}
        </>
      </Box>
      {structuredData && (
        <script
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(buildBoutiqueStoreJsonLd(structuredData)),
          }}
          id='boutiqueStoreJsonLd'
          type='application/ld+json'
        />
      )}
    </>
  );
};

BoutiqueDetailViewWrapper.getLayout = (page: ReactElement) => (
  <DraftModeContext.Provider value={page.props.draftMode}>
    <Default cmsData={page.props.cmsDefaultLayoutData}>{page}</Default>
  </DraftModeContext.Provider>
);
